/**
 * The theme choice: Light, Dark, or follow the operating system.
 *
 * The stored value and the `data-theme` attribute belong to `public/theme-boot.js`, which runs
 * parser-blocking in <head> so the page is never the wrong colour before the canvas exists. This
 * module is the TypeScript half: it reads and writes through `window.jetsonTheme` rather than
 * touching localStorage, so the key name lives in exactly one file.
 *
 * The key is shared with the OpenSilver CRM and the knowledge base - see theme-boot.js for why.
 * That has one consequence worth stating: another tab can change the theme, so this listens for the
 * `storage` event as well as for the OS preference, and repaints when either moves.
 */
import { Desktop } from 'webwin';
import { CrmTheme } from './CrmTheme.js';

export type ThemeChoice = 'light' | 'dark' | 'auto';

/** What theme-boot.js puts on the window. Absent if the script was blocked or failed. */
interface JetsonTheme {
  get(): ThemeChoice;
  set(choice: ThemeChoice): ThemeChoice;
}

function bridge(): JetsonTheme | null {
  const value = (window as unknown as { jetsonTheme?: JetsonTheme }).jetsonTheme;
  return value ?? null;
}

/** The stored choice. Light when nothing is stored, or when theme-boot.js did not run. */
export function currentChoice(): ThemeChoice {
  return bridge()?.get() ?? 'light';
}

/**
 * Whether the effective appearance is dark.
 *
 * Read from the `data-theme` attribute rather than by resolving `auto` here: theme-boot.js already
 * resolved it, and asking `matchMedia` a second time is a second source of truth that can disagree
 * with the attribute the CSS is using.
 */
export function isDark(): boolean {
  return document.documentElement.getAttribute('data-theme') === 'dark';
}

let desktop: Desktop | null = null;
const listeners: Array<(dark: boolean) => void> = [];
const choiceListeners = new Set<(choice: ThemeChoice) => void>();

/** Notified after a repaint, for anything holding a colour it computed itself. */
export function onThemeChanged(fn: (dark: boolean) => void): void {
  listeners.push(fn);
}

/**
 * Notified whenever the stored CHOICE moves - from this tab or another - even when the effective
 * appearance does not. Picking Match system on a light machine changes nothing on screen, so a control
 * showing the choice (the rail's buttons, the Settings combo) cannot learn of it from a repaint.
 * Returns the unsubscribe, since a page's control does not outlive the page.
 */
export function onChoiceChanged(fn: (choice: ThemeChoice) => void): () => void {
  choiceListeners.add(fn);
  return () => choiceListeners.delete(fn);
}

function notifyChoice(): void {
  const choice = currentChoice();
  for (const fn of choiceListeners) fn(choice);
}

/**
 * Starts tracking the theme for a live desktop.
 *
 * One call re-lays-out every control, because a Webwin theme carries metrics as well as paint - so
 * nothing in the application has to be told the theme changed, which is the opposite of the
 * OpenSilver app, where 23 brushes had to be mutated in place and code-built brushes re-resolved.
 */
export function attachTheme(target: Desktop): void {
  desktop = target;

  // Another tab - the knowledge base, or this app in a second tab - changed the shared key.
  window.addEventListener('storage', event => {
    if (event.key === 'jetsoncrm.theme') {
      repaint();
      notifyChoice();
    }
  });

  // Only meaningful while the choice is Auto; theme-boot.js has already re-applied the attribute by
  // the time this fires, so repaint() just reads it.
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (currentChoice() === 'auto') repaint();
  });
}

/** Picks a theme and repaints. Persisted, so it survives a reload and reaches the other tabs. */
export function setChoice(choice: ThemeChoice): void {
  bridge()?.set(choice);
  repaint();
  notifyChoice();
}

function repaint(): void {
  if (!desktop) return;
  const dark = isDark();
  if (desktop.theme instanceof CrmTheme && desktop.theme.isDark === dark) return;
  desktop.setTheme(new CrmTheme(dark));
  for (const fn of listeners) fn(dark);
}
